import React, { useEffect, useState } from "react";
import withStyles from "utils/withStyles";
import styles from "./styles";
import "./styles.css";
import SearchBox from "../common/SearchBox";
import ContentHeader from "../common/ContentHeader";
import Label from "../common/Label";
import TableGenerator from "../../../../../Common/TableGenerator";
import CrudDialog from "../common/CrudDialog";
import { AddButton, DeleteButton, EditButton } from "../common/Buttons";
import { useSnackbar } from "notistack";
import {
  handleServerErrors,
  handleMultiFilterSearch,
} from "../../utiles/helpers";
import { SolutionGuideService } from "../../../../../../services/Api";
import { ALERT_TYPE_OPTIONS } from "./alertTypeOptions";
import { stripHtml } from "utils/helpers";

const filterLabels = {
  alert_type: "alert_type",
  message: "message",
};

const filterFields = ["alert_type", "message"];

const SolutionGuide = () => {
  const classes = styles();
  const [list, setList] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [page, setPage] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [firstPage, setFirstPage] = useState(null);
  const [lastPage, setLastPage] = useState(null);
  const [ordering, setOrdering] = useState("-updated_at");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [filterable, setFilterable] = useState(filterFields);
  const [query, setQuery] = useState("");
  const [loader, setLoader] = useState(false);
  const [dataCount, setDataCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const fields = [
    {
      key: "alert_type",
      columnName: "Alert Type",
      label: "Alert Type",
      type: "select",
      options: ALERT_TYPE_OPTIONS,
      required: true,
      visible: true,
      render: (value) => ALERT_TYPE_OPTIONS.find((o) => o.value === value)?.label || value,
    },
    {
      key: "message",
      columnName: "Alert Message",
      label: "Alert Message",
      type: "text",
      required: true,
      visible: true,
      info: "Matches if this text appears anywhere in the alert reported by the machine, so enter only the stable part &mdash; e.g. <b>Pallet light curtain detection has been blocked</b> also matches <i>1512811 | Pallet light curtain detection has been blocked</i>. If several guides match, the longest one is used.",
    },
    {
      key: "description",
      disableSorting: true,
      columnName: "Description",
      label: "Description",
      type: "text",
      visible: true,
    },
    {
      key: "troubleshooting_steps",
      disableSorting: true,
      columnName: "Troubleshooting Steps",
      label: "Troubleshooting Steps",
      type: "richtext",
      visible: true,
      render: (value) => {
        const plain = stripHtml(value || "");
        return plain.length > 80 ? `${plain.slice(0, 80)}...` : plain || "---";
      },
    },
    {
      key: "support_url",
      disableSorting: true,
      columnName: "Support URL",
      label: "Support URL (image/video link)",
      type: "text",
      visible: true,
    },
    {
      key: "additional_notes",
      disableSorting: true,
      columnName: "Additional Notes",
      label: "Additional Notes",
      type: "text",
      visible: true,
    },
    {
      key: "is_active",
      columnName: "Active",
      label: "Active",
      type: "checkbox",
      visible: true,
      // TableGenerator renders raw booleans, which React prints as nothing,
      // so the column looked empty. Render an explicit label instead.
      render: (value) => (
        <Label color={value ? "primary" : "secondary"}>{value ? "Yes" : "No"}</Label>
      ),
    },
  ];

  const setup = () => {
    setLoader(true);
    setList([]);
    setSelected([]);
  };

  const handleRes = (data) => {
    setList(data.results);
    setNextPage(data.next);
    setPreviousPage(data.previous);
    setFirstPage(data.first);
    setLastPage(data.last);
    setLoader(false);
    setDataCount(data.count);
  };

  const getList = (order, max, customPage = page) => {
    const params = { limit: max ? max : rowsPerPage, ordering: order ? order : ordering, page: customPage + 1 };
    setup();
    SolutionGuideService.list(params)
      .then((data = {}) => handleRes(data))
      .catch((err) => {
        setLoader(false);
        handleServerErrors(err, enqueueSnackbar, "Could not get solution guides. Try again.");
      });
  };

  useEffect(() => {
    getList();
  }, []);

  const handleSearch = (value) => {
    setQuery(value);
    if (value !== "") {
      let searchFilter = handleMultiFilterSearch(filterLabels, filterable, value);
      setup();
      setPage(0);
      SolutionGuideService.list({ ...searchFilter, limit: rowsPerPage, ordering })
        .then((data = {}) => handleRes(data))
        .catch((err) => {
          setLoader(false);
          handleServerErrors(err, enqueueSnackbar, "Could not get solution guides. Try again.");
        });
    } else {
      getList();
    }
  };

  const handleFilter = (arr) => {
    setFilterable(arr);
    if (query !== "") {
      let searchFilter = handleMultiFilterSearch(filterLabels, arr, query);
      setup();
      setPage(0);
      SolutionGuideService.list({ ...searchFilter, limit: rowsPerPage, ordering })
        .then((data = {}) => handleRes(data))
        .catch((err) => {
          setLoader(false);
          handleServerErrors(err, enqueueSnackbar, "Could not get solution guides. Try again.");
        });
    }
  };

  const changePage = async (url) => {
    setup();
    const { data } = await window.axiosIns(url);
    handleRes(data);
  };

  const handleAdd = (data) => {
    SolutionGuideService.create({ ...data })
      .then(() => {
        getList();
        setAddModal(false);
        enqueueSnackbar("Solution guide created successfully.");
      })
      .catch((err) => {
        handleServerErrors(err, enqueueSnackbar, "Could not add solution guide. Try again.");
      });
  };

  const handleEdit = (data) => {
    SolutionGuideService.update({ ...data, id: selected[0].id })
      .then(() => {
        setSelected([]);
        getList();
        setEditModal(false);
        enqueueSnackbar("Solution guide updated successfully.");
      })
      .catch((err) => {
        handleServerErrors(err, enqueueSnackbar, "Could not edit solution guide. Try again.");
      });
  };

  const handleDelete = () => {
    selected.forEach((val, index) => {
      SolutionGuideService.delete({ id: val.id })
        .then(() => {
          if (index + 1 === selected.length) {
            if (list.length - selected.length === 0 && page > 0) {
              setPage(page - 1);
              changePage(previousPage);
            } else {
              getList();
            }
            enqueueSnackbar("Solution guide deleted successfully.");
            setDeleteModal(false);
          }
        })
        .catch((err) => {
          handleServerErrors(err, enqueueSnackbar, "Could not delete solution guide. Try again.");
        });
    });
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <ContentHeader description="Configure and manage solution guides to help operators troubleshoot and resolve alerts." />
      <div className={classes.toolbar}>
        <div className={classes.crudButtons}>
          <AddButton className="mr-3" label="Add" onClick={() => setAddModal(true)} />
          <EditButton disabled={selected.length !== 1} className="mr-3" label="Edit" onClick={() => setEditModal(true)} />
          <DeleteButton disabled={selected.length === 0} className="mr-3" label="Delete" onClick={() => setDeleteModal(true)} />
        </div>
        <div className="d-flex" style={{ marginBottom: "15px" }}>
          <SearchBox
            style={{ width: 290 }}
            placeholder="Search"
            multiple={true}
            query={query}
            onChange={handleFilter}
            fields={filterFields}
            selectedFields={filterable}
            handleSearch={handleSearch}
          />
        </div>
      </div>
      <div className={classes.content}>
        <TableGenerator
          searchQuery={query}
          initialSort={"-updated_at"}
          searchColumnsFilter={true}
          fields={fields}
          loader={loader}
          data={list}
          currentPage={page}
          handleSortChange={(ordering) => {
            setOrdering(ordering);
            getList(ordering);
          }}
          onPageChange={(page, direction) => {
            setPage(page);
            if (direction === "next") changePage(nextPage);
            else if (direction === "back") changePage(previousPage);
            else if (direction === "first") changePage(firstPage);
            else if (direction === "last") changePage(lastPage);
          }}
          backendPagination={true}
          onRowPerPageChange={(rows) => {
            getList(null, rows, 0);
            setRowsPerPage(rows);
            setPage(0);
          }}
          dataCount={dataCount}
          selectedRecords={selected}
          rowOnePage={10}
          onChangeSelected={(selected) => setSelected(selected)}
        />

        <CrudDialog
          title="Add Solution Guide"
          okText="Add"
          description="Configure troubleshooting content for an alert type + message."
          fields={fields}
          onSubmit={(values) => handleAdd(values)}
          open={addModal}
          onClose={() => setAddModal(false)}
        />
        <CrudDialog
          title="Edit Solution Guide"
          okText="Save"
          description="Update the troubleshooting content."
          fields={fields}
          values={selected[0]}
          onSubmit={(values) => handleEdit(values)}
          open={editModal}
          onClose={() => setEditModal(false)}
        />
        <CrudDialog
          title="Delete Solution Guide"
          description="Are you sure you want to delete this solution guide?"
          okText="Delete"
          onSubmit={() => handleDelete()}
          open={deleteModal}
          onClose={() => setDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default withStyles({}, { withTheme: true })(SolutionGuide);
